"use client";

/**
 * Public View Context
 * Provides a way for renderers to detect whether they're in the public view
 * (interactive, no builder UI) vs the builder canvas (static preview, editable).
 * Also carries organizer status, page slug, and the logged-in viewer's identity
 * for admin features (e.g., kebab menus) and signup auto-fill.
 */

import { createContext, useContext } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ActivitySignupMode } from "../../_logic/types/db-types";
import type { ViewerIdentity } from "../../_logic/actions/_shared/get-invited-page-access";
import type { ViewerRegisteredKeys } from "../../_logic/actions/public-actions";

interface PublicViewContextValue {
  /** Whether the component is rendered in the public-facing view */
  isPublic: boolean;
  /** The event ID for the current page */
  eventId: number;
  /** The landing page id for the current page (used for stamping signups). 0 in builder context. */
  landingPageId: number;
  /** Whether the current user is an event organizer (admin or owner) */
  isOrganizer: boolean;
  /** The landing page slug (for building manager URLs) */
  slug: string;
  /** Activity signup mode: internal (built-in registration) or external (booking link) */
  activitySignupMode: ActivitySignupMode;
  /**
   * Logged-in viewer identity, server-resolved from getCurrentUser. Drives
   * signup form prefill so logged-in attendees skip the email/name/phone rows.
   * Null on public pages where the visitor isn't signed in.
   */
  viewer: ViewerIdentity | null;
  /**
   * Opportunistic FK to nowadays.attendees populated when the logged-in
   * viewer's verified email matches a row on the event's roster. Stamped on
   * activity_registrations.attendee_id at signup time.
   */
  attendeeId: number | null;
  /**
   * Whether the viewer may preview `test_publish` activities — event owner/admin, Nowadays
   * admin, or enterprise event access. Gates visibility/signup of `test_publish` activities
   * and their "Test" badge.
   */
  canPreviewTestPublish: boolean;
  /** Page-wide IANA timezone (hero dates, activity slots, schedule, .ics). NULL = floating time. */
  timeZone: string | null;
  /**
   * Activity IDs the logged-in viewer already has an active registration for.
   * Populated by ActivitiesRenderer via getViewerRegistrationsByEvent.
   * Used to disable / annotate CTA buttons on already-signed-up activities.
   */
  signedUpActivityIds: Set<number>;
  /**
   * Setter for signedUpActivityIds — called from ActivitiesRenderer to set the server-fetched
   * values and to merge in just-registered IDs after a successful signup (so anonymous
   * public-page visitors get immediate UI feedback even though server-side identity is null).
   */
  setSignedUpActivityIds: Dispatch<SetStateAction<Set<number>>>;
  /**
   * Time-slot IDs the viewer already has an active registration for.
   * For logged-in viewers: populated from getViewerRegistrationsByEvent.
   * For anonymous public-page visitors: populated optimistically from the just-completed
   * signup. Used by ActivitySignupModal to disable + label slots the viewer has already
   * signed up for when the activity's signup_restriction_mode would block a duplicate.
   */
  signedUpSlotIds: Set<number>;
  /** Setter for signedUpSlotIds — same merge semantics as setSignedUpActivityIds. */
  setSignedUpSlotIds: Dispatch<SetStateAction<Set<number>>>;
  /**
   * Subset of `signedUpSlotIds` the viewer is `waitlisted` (not registered) for. Populated from
   * getViewerRegistrationsByEvent (session-proven viewers); lets the signup modal label a held
   * slot "Waitlisted" instead of "Registered" when the slot was full at signup time.
   */
  waitlistedSlotIds: Set<number>;
  /** Setter for waitlistedSlotIds — set on load/refresh from the authoritative fetch. */
  setWaitlistedSlotIds: Dispatch<SetStateAction<Set<number>>>;
  /**
   * slotId -> registration row id, for the slots this viewer holds. Populated by
   * ActivitiesRenderer via getViewerRegistrationsByEvent and ONLY for session-proven viewers —
   * the registration id is the credential the cancel action authorizes against, so it's empty
   * for anonymous public-page visitors. Its presence is what gates the inline manage/cancel
   * affordance on the card and in the signup modal.
   */
  registrationBySlotId: Map<number, number>;
  /** Setter for registrationBySlotId — set on load/refresh and after a cancel/add reconcile. */
  setRegistrationBySlotId: Dispatch<SetStateAction<Map<number, number>>>;
  /**
   * True when the server could not READ this viewer's registrations, as opposed to reading them
   * and finding none. All four sets above are empty in both cases, so anything that renders a
   * "not signed up" affordance MUST check this first: on a failed read the honest answer is
   * "we couldn't load your registrations", never a Sign Up button offered to someone who already
   * holds a confirmed spot.
   */
  registrationsUnavailable: boolean;
  /** Setter for registrationsUnavailable — cleared once an authoritative refresh succeeds. */
  setRegistrationsUnavailable: Dispatch<SetStateAction<boolean>>;
  /**
   * Monotonic counter bumped after every successful signup/cancel (from any entry point —
   * the Activities section or the schedule's own detail modal). Sections that derive registration
   * state from their own fetch rather than the sets above (e.g. ScheduleRenderer, which needs the
   * status-bucketed `listViewerRegisteredKeys` lookup) key an effect on this so they re-fetch and
   * stay in sync. Acts purely as a change signal — the value itself carries no meaning.
   */
  registrationsVersion: number;
  /** Increments registrationsVersion. Call after any successful signup/cancel reconcile. */
  bumpRegistrations: () => void;
  /**
   * Server-seeded viewer registration keys (registered/waitlisted split) for the schedule pills.
   * `useViewerRegistrations` initializes from this and skips its mount fetch when present. Null in
   * the builder / when the RSC didn't prefetch (the hook then fetches on mount as before).
   */
  initialViewerRegisteredKeys: ViewerRegisteredKeys | null;
}

const PublicViewContext = createContext<PublicViewContextValue | null>(null);

/**
 * Hook to access public view context (public/builder detection, organizer status, slug).
 * Returns inert defaults when used outside a PublicViewProvider (i.e. in the builder canvas).
 *
 * @returns The current PublicViewContextValue or inert defaults.
 */
export function usePublicViewContext(): PublicViewContextValue {
  const ctx = useContext(PublicViewContext);
  return (
    ctx ?? {
      isPublic: false,
      eventId: 0,
      landingPageId: 0,
      isOrganizer: false,
      slug: "",
      activitySignupMode: "internal",
      viewer: null,
      attendeeId: null,
      canPreviewTestPublish: false,
      timeZone: null,
      signedUpActivityIds: new Set<number>(),
      setSignedUpActivityIds: () => {},
      signedUpSlotIds: new Set<number>(),
      setSignedUpSlotIds: () => {},
      waitlistedSlotIds: new Set<number>(),
      setWaitlistedSlotIds: () => {},
      registrationBySlotId: new Map<number, number>(),
      setRegistrationBySlotId: () => {},
      // Builder canvas / no provider: nothing was read, so nothing failed to read.
      registrationsUnavailable: false,
      setRegistrationsUnavailable: () => {},
      registrationsVersion: 0,
      bumpRegistrations: () => {},
      initialViewerRegisteredKeys: null,
    }
  );
}

export { PublicViewContext };
